// ProviderSetup.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaArrowLeft, FaSave, FaCheck, FaChevronRight, FaChevronLeft, FaSpinner } from "react-icons/fa";

function ProviderSetup() {
    const location = useLocation();
    const navigate = useNavigate();
    const provider = location.state?.provider || null;

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [fetchingStep, setFetchingStep] = useState(false);

    // API Configuration for 6 types - empty by default
    const [apiConfigs, setApiConfigs] = useState({
        1: {}, // Recharge API
        2: {}, // Balance API
        3: {}, // Complaint API
        4: {}, // Callback API
        5: {}, // Status API
        6: {}  // Complaint Status API
    });

    // Step configuration
    const steps = [
        { id: 1, label: 'Recharge API', icon: '📱' },
        { id: 2, label: 'Balance API', icon: '💰' },
        { id: 3, label: 'Complaint API', icon: '📝' },
        { id: 4, label: 'Callback API', icon: '🔄' },
        { id: 5, label: 'Status API', icon: '📊' },
        { id: 6, label: 'Complaint Status', icon: '✅' }
    ];

    // Define static fields for each type
    const typeFields = {
        1: { // Recharge API
            req_url: true,
            req_method: true,
            res_format: true,
            req_id: true,
            req_operator_code: true,
            req_amount: true,
            req_number: true,
            res_msg: true,
            res_order_id: false,
            res_balance: false,
            res_txn: true,
            res_status: true,
            res_success: true,
            res_failed: true,
            res_pending: true,
            key: true,
            key_value: true,
            param1_name: true,
            param2_name: true,
            param3_name: true,
            param1_value: true,
            param2_value: true,
            param3_value: true,
            live_com: true,
            live_com_type: true,
            req_complaint_reason: false
        },
        2: { // Balance API
            req_url: true,
            req_method: true,
            res_format: true,
            req_id: false,
            req_operator_code: false,
            req_amount: false,
            req_number: false,
            res_msg: true,
            res_order_id: false,
            res_balance: true,
            res_txn: false,
            res_status: true,
            res_success: true,
            res_failed: true,
            res_pending: true,
            key: true,
            key_value: true,
            param1_name: true,
            param2_name: true,
            param3_name: true,
            param1_value: true,
            param2_value: true,
            param3_value: true,
            live_com: false,
            live_com_type: false,
            req_complaint_reason: false
        },
        3: { // Complaint API
            req_url: true,
            req_method: true,
            res_format: true,
            req_id: true,
            req_operator_code: false,
            req_amount: false,
            req_number: false,
            res_msg: true,
            res_order_id: false,
            res_balance: false,
            res_txn: false,
            res_status: true,
            res_success: true,
            res_failed: true,
            res_pending: true,
            key: true,
            key_value: true,
            param1_name: true,
            param2_name: true,
            param3_name: true,
            param1_value: true,
            param2_value: true,
            param3_value: true,
            live_com: false,
            live_com_type: false,
            req_complaint_reason: true
        },
        4: { // Callback API
            req_url: false,
            req_method: true,
            res_format: true,
            req_id: false,
            req_operator_code: false,
            req_amount: false,
            req_number: false,
            res_msg: false,
            res_order_id: true,
            res_balance: false,
            res_txn: true,
            res_status: true,
            res_success: true,
            res_failed: true,
            res_pending: true,
            key: false,
            key_value: false,
            param1_name: false,
            param2_name: false,
            param3_name: false,
            param1_value: false,
            param2_value: false,
            param3_value: false,
            live_com: false,
            live_com_type: false,
            req_complaint_reason: false
        },
        5: { // Status API
            req_url: true,
            req_method: true,
            res_format: true,
            req_id: true,
            req_operator_code: false,
            req_amount: false,
            req_number: false,
            res_msg: true,
            res_order_id: false,
            res_balance: false,
            res_txn: true,
            res_status: true,
            res_success: true,
            res_failed: true,
            res_pending: true,
            key: true,
            key_value: true,
            param1_name: true,
            param2_name: true,
            param3_name: true,
            param1_value: true,
            param2_value: true,
            param3_value: true,
            live_com: true,
            live_com_type: true,
            req_complaint_reason: false
        },
        6: { // Complaint Status API
            req_url: true,
            req_method: true,
            res_format: true,
            req_id: true,
            req_operator_code: false,
            req_amount: false,
            req_number: false,
            res_msg: true,
            res_order_id: false,
            res_balance: false,
            res_txn: true,
            res_status: true,
            res_success: true,
            res_failed: true,
            res_pending: true,
            key: true,
            key_value: true,
            param1_name: true,
            param2_name: true,
            param3_name: true,
            param1_value: true,
            param2_value: true,
            param3_value: true,
            live_com: false,
            live_com_type: false,
            req_complaint_reason: false
        }
    };

    // Fetch configuration for a specific step/type
    const fetchStepConfig = async (type) => {
        if (!provider) return;

        setFetchingStep(true);
        try {
            const result = await apiCall(
                "get-provider-config-by-type",
                {
                    provider_id: provider.id,
                    type: type
                },
                true
            );

            if (result.status === true && result.data) {
                // Filter out null values before setting
                const filteredData = {};
                Object.keys(result.data).forEach(key => {
                    if (result.data[key] !== null && result.data[key] !== 'NULL') {
                        filteredData[key] = result.data[key];
                    }
                });

                setApiConfigs(prev => ({
                    ...prev,
                    [type]: filteredData
                }));
            } else {
                setApiConfigs(prev => ({
                    ...prev,
                    [type]: {}
                }));
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setApiConfigs(prev => ({
                ...prev,
                [type]: {}
            }));
        } finally {
            setFetchingStep(false);
        }
    };

    // Save current step and move to next
    const handleSaveAndNext = async () => {
        if (!provider) {
            toast.error("Provider information missing");
            return;
        }

        const type = currentStep;
        const config = apiConfigs[type];

        // Check if there's any data to save
        if (!config || Object.keys(config).length === 0) {
            toast.error("No configuration to save");
            return;
        }

        setSaving(true);
        try {
            const configData = {
                provider_id: provider.id,
                type: type,
                ...config
            };

            const result = await apiCall(
                "update-provider-config",
                { config: configData },
                true
            );

            if (result.status === true) {
                toast.success(`${steps[currentStep - 1].label} saved successfully!`);

                // Check if this is the last step
                if (currentStep === 6) {
                    toast.success("All API configurations completed!");
                    setTimeout(() => navigate(-1));
                } else {
                    // Move to next step
                    const nextStep = currentStep + 1;
                    setCurrentStep(nextStep);
                    await fetchStepConfig(nextStep);
                }
            } else {
                toast.error(result.message || `Failed to save ${steps[currentStep - 1].label}`);
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.error("Network error. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // Handle previous step
    const handlePrev = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Initial fetch for step 1
    useEffect(() => {
        if (provider) {
            fetchStepConfig(1);
        }
    }, [provider]);

    const handleConfigChange = (type, field, value) => {
        setApiConfigs(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [field]: value
            }
        }));
    };

    // Check if a field should be shown for current type
    const shouldShowField = (fieldName) => {
        const type = currentStep;
        return typeFields[type] && typeFields[type][fieldName] === true;
    };

    const renderStepContent = () => {
        const type = currentStep;
        const config = apiConfigs[type] || {};

        if (fetchingStep) {
            return (
                <div className="flex justify-center py-12">
                    <div className="text-center">
                        <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
                        <p className="text-gray-600">Loading configuration...</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {/* Common Fields */}
                {shouldShowField('req_url') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                API URL <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={config.req_url || ''}
                                onChange={(e) => handleConfigChange(type, 'req_url', e.target.value)}
                                placeholder="https://api.example.com/endpoint"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                        </div>

                        {shouldShowField('req_method') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Request Method
                                </label>
                                <select
                                    value={config.req_method || 'POST'}
                                    onChange={(e) => handleConfigChange(type, 'req_method', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                >
                                    <option value="POST">POST</option>
                                    <option value="GET">GET</option>
                                    <option value="TEXT">TEXT</option>
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {shouldShowField('res_format') && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Response Format
                        </label>
                        <select
                            value={config.res_format || 'JSON'}
                            onChange={(e) => handleConfigChange(type, 'res_format', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                            <option value="JSON">JSON</option>
                            <option value="XML">XML</option>
                        </select>
                    </div>
                )}

                {/* Request Parameters Mapping */}
                {(shouldShowField('req_id') || shouldShowField('req_operator_code') ||
                    shouldShowField('req_amount') || shouldShowField('req_number') ||
                    shouldShowField('req_complaint_reason')) && (
                        <>
                            <h4 className="font-medium text-gray-700 border-t pt-4">Request Parameters Mapping</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {shouldShowField('req_id') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                                        <input
                                            type="text"
                                            value={config.req_id || ''}
                                            onChange={(e) => handleConfigChange(type, 'req_id', e.target.value)}
                                            placeholder="order_id"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('req_operator_code') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Operator Code</label>
                                        <input
                                            type="text"
                                            value={config.req_operator_code || ''}
                                            onChange={(e) => handleConfigChange(type, 'req_operator_code', e.target.value)}
                                            placeholder="operator_id"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('req_amount') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                        <input
                                            type="text"
                                            value={config.req_amount || ''}
                                            onChange={(e) => handleConfigChange(type, 'req_amount', e.target.value)}
                                            placeholder="amount"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('req_number') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Number</label>
                                        <input
                                            type="text"
                                            value={config.req_number || ''}
                                            onChange={(e) => handleConfigChange(type, 'req_number', e.target.value)}
                                            placeholder="number"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('req_complaint_reason') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Complaint Reason</label>
                                        <input
                                            type="text"
                                            value={config.req_complaint_reason || ''}
                                            onChange={(e) => handleConfigChange(type, 'req_complaint_reason', e.target.value)}
                                            placeholder="reason"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                {/* Response Parameters Mapping */}
                {(shouldShowField('res_msg') || shouldShowField('res_order_id') ||
                    shouldShowField('res_balance') || shouldShowField('res_txn') ||
                    shouldShowField('res_status')) && (
                        <>
                            <h4 className="font-medium text-gray-700 border-t pt-4">Response Parameters Mapping</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {shouldShowField('res_msg') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                        <input
                                            type="text"
                                            value={config.res_msg || ''}
                                            onChange={(e) => handleConfigChange(type, 'res_msg', e.target.value)}
                                            placeholder="message"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('res_order_id') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                                        <input
                                            type="text"
                                            value={config.res_order_id || ''}
                                            onChange={(e) => handleConfigChange(type, 'res_order_id', e.target.value)}
                                            placeholder="order_id"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('res_balance') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Balance</label>
                                        <input
                                            type="text"
                                            value={config.res_balance || ''}
                                            onChange={(e) => handleConfigChange(type, 'res_balance', e.target.value)}
                                            placeholder="balance"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('res_txn') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                                        <input
                                            type="text"
                                            value={config.res_txn || ''}
                                            onChange={(e) => handleConfigChange(type, 'res_txn', e.target.value)}
                                            placeholder="transaction_id"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('res_status') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status Field</label>
                                        <input
                                            type="text"
                                            value={config.res_status || ''}
                                            onChange={(e) => handleConfigChange(type, 'res_status', e.target.value)}
                                            placeholder="transaction_status"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                {/* Status Keywords */}
                {(shouldShowField('res_success') || shouldShowField('res_failed') || shouldShowField('res_pending')) && (
                    <div className="border-t pt-4">
                        <h4 className="font-medium text-gray-700 mb-3">Status Keywords</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {shouldShowField('res_success') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Success</label>
                                    <input
                                        type="text"
                                        value={config.res_success || ''}
                                        onChange={(e) => handleConfigChange(type, 'res_success', e.target.value)}
                                        placeholder="Success"
                                        className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm bg-green-50"
                                    />
                                </div>
                            )}
                            {shouldShowField('res_failed') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Failed</label>
                                    <input
                                        type="text"
                                        value={config.res_failed || ''}
                                        onChange={(e) => handleConfigChange(type, 'res_failed', e.target.value)}
                                        placeholder="Failed"
                                        className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-red-50"
                                    />
                                </div>
                            )}
                            {shouldShowField('res_pending') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Pending</label>
                                    <input
                                        type="text"
                                        value={config.res_pending || ''}
                                        onChange={(e) => handleConfigChange(type, 'res_pending', e.target.value)}
                                        placeholder="Processed"
                                        className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm bg-yellow-50"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Authentication Keys */}
                {(shouldShowField('key') || shouldShowField('key_value')) && (
                    <div className="border-t pt-4">
                        <h4 className="font-medium text-gray-700 mb-3">Authentication</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {shouldShowField('key') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                                    <input
                                        type="text"
                                        value={config.key || ''}
                                        onChange={(e) => handleConfigChange(type, 'key', e.target.value)}
                                        placeholder="user_id"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>
                            )}
                            {shouldShowField('key_value') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Key Value</label>
                                    <input
                                        type="text"
                                        value={config.key_value || ''}
                                        onChange={(e) => handleConfigChange(type, 'key_value', e.target.value)}
                                        placeholder="API_KEY_VALUE"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Additional Parameters */}
                {(shouldShowField('param1_name') || shouldShowField('param2_name') || shouldShowField('param3_name') ||
                    shouldShowField('param1_value') || shouldShowField('param2_value') || shouldShowField('param3_value')) && (
                        <div className="border-t pt-4">
                            <h4 className="font-medium text-gray-700 mb-3">Additional Parameters</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {shouldShowField('param1_name') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Parameter 1 Name</label>
                                        <input
                                            type="text"
                                            value={config.param1_name || ''}
                                            onChange={(e) => handleConfigChange(type, 'param1_name', e.target.value)}
                                            placeholder="param1_name"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('param1_value') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Parameter 1 Value</label>
                                        <input
                                            type="text"
                                            value={config.param1_value || ''}
                                            onChange={(e) => handleConfigChange(type, 'param1_value', e.target.value)}
                                            placeholder="param1_value"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('param2_name') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Parameter 2 Name</label>
                                        <input
                                            type="text"
                                            value={config.param2_name || ''}
                                            onChange={(e) => handleConfigChange(type, 'param2_name', e.target.value)}
                                            placeholder="param2_name"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('param2_value') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Parameter 2 Value</label>
                                        <input
                                            type="text"
                                            value={config.param2_value || ''}
                                            onChange={(e) => handleConfigChange(type, 'param2_value', e.target.value)}
                                            placeholder="param2_value"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('param3_name') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Parameter 3 Name</label>
                                        <input
                                            type="text"
                                            value={config.param3_name || ''}
                                            onChange={(e) => handleConfigChange(type, 'param3_name', e.target.value)}
                                            placeholder="param3_name"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                                {shouldShowField('param3_value') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Parameter 3 Value</label>
                                        <input
                                            type="text"
                                            value={config.param3_value || ''}
                                            onChange={(e) => handleConfigChange(type, 'param3_value', e.target.value)}
                                            placeholder="param3_value"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                {/* Live/Commission Fields */}
                {(shouldShowField('live_com') || shouldShowField('live_com_type')) && (
                    <div className="border-t pt-4">
                        <h4 className="font-medium text-gray-700 mb-3">Live / Commission Settings</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {shouldShowField('live_com') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Live Commission</label>
                                    <input
                                        type="text"
                                        value={config.live_com || ''}
                                        onChange={(e) => handleConfigChange(type, 'live_com', e.target.value)}
                                        placeholder="Live Commission"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    />
                                </div>
                            )}
                            {shouldShowField('live_com_type') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Live Commission Type</label>
                                    <select
                                        value={config.live_com_type || '1'}
                                        onChange={(e) => handleConfigChange(type, 'live_com_type', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        <option value="1">Percentage</option>
                                        <option value="2">Flat</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!provider) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800">No Provider Selected</h2>
                    <p className="text-gray-600 mt-2">Please select a provider from the list</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Go to Providers
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Header */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {provider.name}
                            </h1>
                            <p className="text-gray-600 text-sm">
                                Configure API endpoints for {provider.name}
                            </p>
                        </div>
                    </div>

                    {/* Progress Steps */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex items-center justify-between">
                            {steps.map((step, index) => (
                                <React.Fragment key={step.id}>
                                    <div
                                        className={`flex flex-col items-center gap-1 flex-1 transition-all ${currentStep === step.id ? 'text-blue-600' :
                                            currentStep > step.id ? 'text-green-500' : 'text-gray-400'
                                            }`}
                                    >
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-semibold transition-all
                                            ${currentStep === step.id ? 'bg-blue-600 text-white' :
                                                currentStep > step.id ? 'bg-green-500 text-white' :
                                                    'bg-gray-200 text-gray-600'}`}>
                                            {currentStep > step.id ? <FaCheck /> : step.id}
                                        </div>
                                        <span className="text-xs font-medium text-center hidden sm:block">{step.label}</span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-300'}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Step {currentStep}: {steps[currentStep - 1].label}
                            </h3>
                            <p className="text-sm text-gray-600">
                                Configure {steps[currentStep - 1].label} API settings
                            </p>
                        </div>

                        {renderStepContent()}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="mt-6 flex justify-between">
                        <button
                            onClick={handlePrev}
                            disabled={currentStep === 1 || fetchingStep}
                            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <FaChevronLeft /> Previous
                        </button>
                        <button
                            onClick={handleSaveAndNext}
                            disabled={saving || fetchingStep}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <FaSpinner className="animate-spin h-4 w-4" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    {currentStep === 6 ? 'Finish' : 'Save & Next'}
                                    {currentStep < 6 && <FaChevronRight />}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <Toaster position="top-right" />
        </div>
    );
}

export default ProviderSetup;